import { JsonPatch } from "json8-patch";
import { flow, types, getEnv, Instance } from "mobx-state-tree";

import { DamageDetails } from "./DamageDetails";
import { ImageWithPreview } from "../../components/image-upload/ImageUpload";
import { getImageFormData } from "../form/FormAppenders";
import { currentUserService } from "../user/CurrentUserStorage";
import { DamageDetailsResponse } from "../../api-models/DamageDetailsResponse";
import { endpoints } from "../api/ApiEndpoints";
import { StorageModel } from "../storage/StorageModel";
import { permissionsService } from "../authorization/UserPermissionsService";
import {
  DeleteDamage,
  DeleteDamageFromAssignedProject,
  EditDamage,
  EditDamageFromAssignedProject,
} from "../authorization/Permissions";

export const emptyDamage: DamageDetailsResponse = {
  id: 0,
  sectionId: "",
  inspectionId: "",
  inspectionLockDetails: null,
  dynamicProperties: [],
  mainCodeFieldIndicator: "",
  c1CodeFieldIndicator: "",
  c2CodeFieldIndicator: "",
  quantification1FieldIndicator: "",
  quantification2FieldIndicator: "",
  image: undefined,
  videoDetails: {
    videoPosition: "",
    station: "",
    inspectionCode: "",
  },
  created: new Date(),
  createdBy: {
    id: 0,
    name: "",
    isDeleted: false,
  },
  genericIssues: [],
};

const CurrentDamageModel = types.model({
  currentDamageId: types.frozen<number>(),
  currentDamage: types.optional(types.frozen<DamageDetails>(), new DamageDetails(emptyDamage)),
});

export const CurrentDamageStorageModel = types
  .compose(CurrentDamageModel, StorageModel)
  .named("CurrentDamageStorageModel")
  .actions((self) => {
    return {
      mapResponse(response: DamageDetailsResponse) {
        self.currentDamage = new DamageDetails(response);
      },
      deleteDamage: flow(function* deleteDamage() {
        return yield getEnv(self).api.delete(self.endpoint);
      }),
      editDamageDetails: flow(function* editDamageDetails(damagePatch: JsonPatch) {
        if (!damagePatch.length) return;

        return yield getEnv(self).api.patch(self.endpoint, damagePatch);
      }),
      editDamageImage: flow(function* editDamageImage(image: ImageWithPreview) {
        const imageForm = getImageFormData(image);
        const endpoint = endpoints.get("damage_file");
        endpoint.routeParams = [self.currentDamageId];

        return yield getEnv(self).api.put(endpoint, imageForm);
      }),
      deleteDamageImage: flow(function* deleteDamageImage() {
        const endpoint = endpoints.get("damage_file");
        endpoint.routeParams = [self.currentDamageId];

        return yield getEnv(self).api.delete(endpoint);
      }),
    };
  })
  .views((self) => ({
    get endpoint() {
      const endpoint = endpoints.get("damage");
      endpoint.routeParams = [self.currentDamageId];
      return endpoint;
    },
    get isLoading() {
      return self.currentDamage.id <= 0;
    },
    get damageIsLocked() {
      return !!self.currentDamage.inspectionLockDetails;
    },
    get userWithLockOnDamage() {
      return self.currentDamage.inspectionLockDetails?.owner;
    },
    get currentUserHasLockOnDamage() {
      return (
        self.currentDamage.inspectionLockDetails?.owner.id === currentUserService.currentUser.id
      );
    },
    get canUserEditDamage() {
      return permissionsService.userHasOneOfThePermissions([
        EditDamage,
        EditDamageFromAssignedProject,
      ]);
    },
    get imageUrl() {
      return self.currentDamage.image?.url || "";
    },
  }))
  .views((self) => ({
    get canSubmitEdit() {
      return (!self.damageIsLocked || self.currentUserHasLockOnDamage) && self.canUserEditDamage;
    },
    get canUserDeleteDamage() {
      return (
        (!self.damageIsLocked || self.currentUserHasLockOnDamage) &&
        permissionsService.userHasOneOfThePermissions([
          DeleteDamage,
          DeleteDamageFromAssignedProject,
        ])
      );
    },
    get damageHasImageUploaded() {
      return self.imageUrl;
    },
  }));

export type CurrentDamageStorage = Instance<typeof CurrentDamageStorageModel>;
